import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import {
  leadListsApi,
  geosearchApi,
  addressSearchApi,
} from "../../../libs/landvoice-api";
import {
  SearchPropertiesOptions,
  SearchFilters,
  SearchRegion,
  LeadList,
} from "../../../types/neighborhoodSearch";
import { getGeoSearchParams } from "../utils/getGeoSearchParams";
import { NS_LIST_SAVED_SEARCHES_CACHE_KEY, NS_CREATE_SAVED_SEARCH_MUTATION_KEY } from "../constants/cacheKeys";

export const useCreateSavedSearchMutation = (): UseMutationResult<
  LeadList,
  Error,
  {
    listName: string;
    listOptions: SearchPropertiesOptions | null;
    searchRegion?: SearchRegion;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation<
    LeadList,
    Error,
    {
      listName: string;
      listOptions: SearchPropertiesOptions | null;
      searchRegion?: SearchRegion;
    }
  >({
    mutationKey: [NS_CREATE_SAVED_SEARCH_MUTATION_KEY],
    mutationFn: async ({
      listName,
      listOptions,
      searchRegion,
    }: {
      listName: string;
      listOptions: SearchPropertiesOptions | null;
      searchRegion?: SearchRegion;
    }) => {
      const { geo, address, addressLocation } = listOptions || {};

      let searchParameters = {};
      let response;
      if (geo) {
        const {
          bedrooms,
          bathrooms,
          acreage,
          squareFootage,
          yearBuilt,
          propertyType,
          companyOwned,
        } = getGeoSearchParams(listOptions);

        response = await geosearchApi.geosearch(
          geo,
          bedrooms,
          bathrooms,
          acreage,
          squareFootage,
          yearBuilt,
          propertyType,
          companyOwned
        );

        searchParameters = {
          searchFilters: {
            acreage: {
              min: listOptions?.acreage?.[0],
              max: listOptions?.acreage?.[1],
            },
            bedrooms: {
              min: listOptions?.bedrooms?.[0],
              max: listOptions?.bedrooms?.[1],
            },
            bathrooms: {
              min: listOptions?.bathrooms?.[0],
              max: listOptions?.bathrooms?.[1],
            },
            yearBuilt: {
              min: listOptions?.yearBuilt?.[0],
              max: listOptions?.yearBuilt?.[1],
            },
            propertyType: listOptions?.propertyType?.join(","),
            companyOwned: listOptions?.companyOwned,
            squareFootage: {
              min: listOptions?.squareFootage?.[0],
              max: listOptions?.squareFootage?.[1],
            },
          } as SearchFilters,
          searchRegion,
        };
      } else if (address) {
        response = await addressSearchApi.reverseAddressLookup({
          addresses: [
            {
              house_number: address.houseNumber,
              street_name: address.streetName,
              street_suffix: address.streetSuffix,
              city: address.city,
              state: address.state,
              postal_code: address.postalCode,
              unit: address.unit,
            },
          ],
        });

        searchParameters = {
          searchAddress: {
            houseNumber: address.houseNumber,
            streetName: address.streetName,
            streetSuffix: address.streetSuffix,
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
            postalCodePlus4: address.postalCodePlus4,
            streetPostDirection: address.streetPostDirection,
            streetPreDirection: address.streetPreDirection,
            unit: address.unit,
            location: addressLocation,
          },
        };
      } else {
        throw new Error("Either geo or address must be provided");
      }

      if (!response.data.id) {
        throw new Error("Failed to create saved search");
      }

      const newListId = response.data.id;
      const { data: patchedList } = await leadListsApi.patchList(newListId, {
        name: listName,
        search_parameters: searchParameters,
      });
      return patchedList;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [NS_LIST_SAVED_SEARCHES_CACHE_KEY],
      });
    },
  });
};